import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

interface MetricData {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  delta: number;
  id: string;
  entries: PerformanceEntry[];
}

/**
 * Send metrics to analytics endpoint
 */
async function sendMetric(metric: MetricData) {
  // In production, send to analytics service
  if (import.meta.env.PROD) {
    try {
      await fetch('/metrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          metric: metric.name,
          value: metric.value,
          rating: metric.rating,
          timestamp: Date.now(),
          url: window.location.href,
          userAgent: navigator.userAgent,
        }),
      });
    } catch (error) {
      console.error('Failed to send metric:', error);
    }
  }

  // Always log to console in development
  if (import.meta.env.DEV) {
    console.log(`[Performance] ${metric.name}:`, {
      value: `${metric.value.toFixed(2)}ms`,
      rating: metric.rating,
      delta: `${metric.delta.toFixed(2)}ms`,
    });
  }
}

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring() {
  // Core Web Vitals
  onCLS(sendMetric);
  onINP(sendMetric);
  onFCP(sendMetric);
  onLCP(sendMetric);
  onTTFB(sendMetric);

  // Custom performance marks
  if (typeof window !== 'undefined' && window.performance) {
    // Mark when app becomes interactive
    window.addEventListener('load', () => {
      performance.mark('app-interactive');

      // Measure time to interactive
      const navigationEntry = performance.getEntriesByType(
        'navigation',
      )[0] as PerformanceNavigationTiming;
      if (navigationEntry) {
        const tti = performance.now();
        console.log('[Performance] Time to Interactive:', `${tti.toFixed(2)}ms`);

        // Send custom metric
        sendMetric({
          name: 'TTI',
          value: tti,
          rating: tti < 3000 ? 'good' : tti < 5000 ? 'needs-improvement' : 'poor',
          delta: 0,
          id: `tti-${Date.now()}`,
          entries: [],
        });
      }
    });

    // Monitor long tasks
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              console.warn('[Performance] Long task detected:', {
                duration: `${entry.duration.toFixed(2)}ms`,
                startTime: `${entry.startTime.toFixed(2)}ms`,
              });
            }
          }
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch {
        // Long task observer not supported
      }
    }
  }
}

/**
 * Performance budgets
 */
export const performanceBudgets = {
  lcp: 2500, // Largest Contentful Paint
  fid: 100, // First Input Delay (replaced by INP)
  inp: 200, // Interaction to Next Paint
  cls: 0.1, // Cumulative Layout Shift
  ttfb: 800, // Time to First Byte
  fcp: 1800, // First Contentful Paint
  tti: 3800, // Time to Interactive
  bundleSize: 200 * 1024, // 200KB for main bundle
};

/**
 * Check if performance meets budgets
 */
export function checkPerformanceBudgets(metrics: Record<string, number>): {
  passed: boolean;
  violations: string[];
} {
  const violations: string[] = [];

  Object.entries(metrics).forEach(([key, value]) => {
    const budget = performanceBudgets[key as keyof typeof performanceBudgets];
    if (budget && value > budget) {
      violations.push(`${key.toUpperCase()}: ${value.toFixed(2)} (budget: ${budget})`);
    }
  });

  return {
    passed: violations.length === 0,
    violations,
  };
}
